import { RestError, RestErrorData } from "api/restClient.types";

/**
 * Description of a REST error, ready for the notification layer to render.
 *
 * - `structured`: the response carried our normal JSON error body ({title, message}).
 *   The existing title/details rendering applies.
 * - `friendly`: the response body was NOT our structured JSON — a raw gateway/proxy
 *   HTML page (502/503/504 while the backend is restarting/unreachable), an empty body,
 *   or no response at all (network failure). We show a short, status-derived line
 *   instead of dumping the raw body, keeping the raw body (if any) as collapsed details.
 */
export type RestErrorDescription =
    | { kind: "structured"; title?: string; message?: string }
    | { kind: "friendly"; title: string; message: string; rawDetails?: string };

const hasStructuredBody = (data: unknown): data is RestErrorData =>
    data !== null && typeof data === "object";

/** A short, plain, status-derived message for a non-structured / gateway error. */
export const friendlyServerMessage = (status?: number, statusText?: string): string => {
    const code = status ? ` (${status}${statusText ? ` ${statusText}` : ""})` : "";
    if (status === 503) {
        return `The server is temporarily unavailable${code}. Please wait a moment and try again.`;
    }
    if (!status || status === 502 || status === 504) {
        return `Could not reach the server${code}. Please check your connection and try again.`;
    }
    return `The server returned an unexpected response${code}. Please try again.`;
};

/**
 * Classify a REST error into something renderable. Pure (no React) so it can be
 * unit-tested directly.
 */
export const describeRestError = (
    error: RestError | undefined,
    titleOverride?: string,
): RestErrorDescription => {
    // The declared type says `data` is our structured RestErrorData, but a gateway/proxy
    // error returns a raw string (HTML) body at runtime — treat it as unknown and sniff it.
    const { status, statusText, data } = (error?.response ?? {}) as {
        status?: number;
        statusText?: string;
        data?: unknown;
    };

    if (hasStructuredBody(data)) {
        return { kind: "structured", title: data.title, message: data.message };
    }

    const rawStringBody = typeof data === "string" && data.trim() ? data : undefined;
    return {
        kind: "friendly",
        title: titleOverride ?? "Something went wrong",
        message: friendlyServerMessage(status, statusText),
        rawDetails: rawStringBody,
    };
};
